import { useState } from 'react';
import AnimatedSection from '../../../components/base/AnimatedSection';
import type { ReactNode } from 'react';

const faqs: { q: string; a: ReactNode }[] = [
  {
    q: 'What files do I receive after purchasing a beat?',
    a: <>You receive a high-quality WAV file and your exclusive license agreement document immediately after payment. Need an MP3? Simply <a href="sms:+15716995343" className="font-semibold text-gray-800 underline whitespace-nowrap">text +1 (571)-699-5343</a> and we will deliver it as soon as possible. The full stem pack (individual drum, melody, bass, and sample tracks) is delivered to your email within 24 hours.</>,
  },
  {
    q: 'Can I sign with a major record label using this beat?',
    a: 'Yes, absolutely. Your exclusive license is fully compatible with any major or independent label deal. Labels routinely clear beats through exclusive licensing agreements — yours is exactly that. Simply provide your label with a copy of your license document.',
  },
  {
    q: 'Is there an expiration date on the license?',
    a: 'No. Your exclusive license never expires. Once purchased, you hold commercial rights to that instrumental permanently. There are no renewal fees, no annual charges, and no hidden conditions.',
  },
  {
    q: 'What is the 5% producer royalty and how does it work?',
    a: 'This is a standard industry practice where the producer retains a small share of performance and mechanical royalties collected through a PRO (ASCAP, BMI, or SESAC). It only applies to royalties collected via PRO registration. You keep 95% of all your earnings. This does not affect your streaming revenue, sales income, or sync deals.',
  },
  {
    q: 'Can I use the beat in a TV show, film, or commercial?',
    a: 'Yes. Your exclusive license covers sync use. If a production company or supervisor needs clearance documentation, we provide a formal sync clearance letter at no extra charge. Just reach out and we will turn it around quickly.',
  },
  {
    q: 'What if I purchase a beat and another artist already released with it?',
    a: 'This cannot happen. When you purchase an exclusive license, the beat is permanently removed from the store before your transaction is fully processed. We do not sell the same beat twice — ever. Exclusive means exclusive.',
  },
  {
    q: "What's the difference between exclusive and non-exclusive licensing?",
    a: 'NCMG only offers exclusive licenses. Exclusive means you are the only artist in the world with rights to that instrumental — the beat is pulled from sale permanently. Non-exclusive licenses (offered by other platforms) allow the same beat to be sold to dozens or even hundreds of artists simultaneously, meaning multiple songs can exist using the identical instrumental.',
  },
  {
    q: 'Do I need to credit the producer on my releases?',
    a: 'Yes. You must include producer credit in your release metadata and liner notes. The standard format is: "Produced by JayMaximus / NCMG Productions." This is a condition of the license and is also required for the producer\'s PRO royalty collection.',
  },
];

export default function LicenseFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <AnimatedSection animation="fade-up" className="text-center mb-16">
          <div className="inline-block px-4 py-2 border border-gray-300 rounded-full text-xs font-semibold text-gray-700 mb-4 tracking-widest uppercase">
            FAQ
          </div>
          <h2 className="text-6xl font-bold text-gray-900 mb-4 leading-tight">
            Common Questions<br />Answered.
          </h2>
          <p className="text-lg text-gray-500">
            Everything artists ask before pulling the trigger. Straight answers, no fluff.
          </p>
        </AnimatedSection>

        <div className="space-y-3">
          {faqs.map((faq, i) => {
            const isOpen = openIdx === i;
            return (
              <AnimatedSection key={i} animation="fade-up" delay={i * 60} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer group"
                >
                  <span className={`text-base font-semibold pr-6 leading-snug transition-colors ${isOpen ? 'text-gray-900' : 'text-gray-800 group-hover:text-gray-900'}`}>
                    {faq.q}
                  </span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${isOpen ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'}`}>
                    <i className={`text-sm transition-transform duration-300 ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </div>
                </button>

                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: isOpen ? '300px' : '0px', opacity: isOpen ? 1 : 0 }}
                >
                  <p className="px-7 pb-6 text-sm text-gray-600 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
}
